import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstant from "../../../../../api";
import { listProductPdpUrlScores } from "../../../../../api/endpoints";
import type { PdpUrlScore } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import { pageScoreHistory } from "./pageModel";

/**
 * One page's ready scores — `GET …/pdp-urls/:id/scores` (endpoints.ts:425),
 * the same source the modal's PdpScoreHistory and the video page read.
 *
 * The API answers OLDEST first (desirelist-api pdpUrls.js:551); the adapter
 * (`pageScoreHistory`) drops runs without `scored_at` and reverses, so `[0]`
 * is now — what `movement`, `subDelta` and `trendPoints` expect
 * (pageScore.ts:305-335). The video page's useScoreHistory does the same
 * (pageScore.ts:146-159).
 *
 * Refetches when the row's latest ready score changes (a rescore just landed —
 * PdpScoreHistory's trigger, and the video page's useScoreHistory deps,
 * pageScore.ts:168) and on Try again. That is every change the series can
 * see: the endpoint lists ready runs only, the row's `latest_score` is the
 * newest ready one, a ready score is never deleted, and a failed score (the
 * sync 502 included) writes an error attempt, never a ready row. A refetch
 * keeps the old series on screen until the new one replaces it; a failed
 * refetch keeps it too, with `error`.
 *
 * `reloadKey` is the page's settings-modal close. It re-reads only a history
 * whose last load FAILED (silently, as Try again would): one that loaded is
 * still current by the rule above.
 *
 * `enabled` = the page has a score. An unscored page has no series to show,
 * so it makes no call. The block is keyed by its page and a product switch
 * unmounts the section, so `productId` and `pdpUrlId` never change under one
 * history.
 */

interface PageScoreHistoryState {
  /** Newest first. Null until the first load lands (or when it failed with nothing loaded). */
  history: PdpUrlScore[] | null;
  loading: boolean;
  error: boolean;
  reload: () => void;
}

export const usePageScoreHistory = (
  productId: number,
  pdpUrlId: number,
  latestScoreId: number | null | undefined,
  reloadKey: number,
  enabled: boolean,
): PageScoreHistoryState => {
  const [history, setHistory] = useState<PdpUrlScore[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || !productId || !pdpUrlId) {
      setHistory(null);
      setLoading(false);
      setError(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    axiosInstant
      .get(listProductPdpUrlScores(productId, pdpUrlId))
      .then(({ data }) => {
        if (!live) return;
        setHistory(pageScoreHistory(data));
        setError(false);
      })
      .catch(() => {
        if (live) setError(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [enabled, productId, pdpUrlId, latestScoreId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // Settings closed: a failed history tries again; a loaded one stays. Only
  // on a changed key, never on mount.
  const seenReloadKey = useRef(reloadKey);
  useEffect(() => {
    if (seenReloadKey.current === reloadKey) return;
    seenReloadKey.current = reloadKey;
    if (error) reload();
  }, [reloadKey, error, reload]);

  return { history, loading, error, reload };
};
